/**
 * Purpose: {@link LlmClient} — a typed JSON-RPC 2.0 client for the LLM
 *          service. Wraps `fetch` so callers get method-by-method call
 *          signatures sourced from @ada/llm's domain types instead of
 *          building envelopes by hand. Works in Node.js and in the browser.
 *
 * Implementation notes:
 *   - Isomorphic by design: uses only `globalThis.fetch`,
 *     `crypto.randomUUID()`, and `AbortSignal` — all native in Node 18+
 *     and modern browsers. No `node:*` imports.
 *   - All type imports from `@ada/llm` go through `import type`; with
 *     `verbatimModuleSyntax: true` (inherited from tsconfig.base.json)
 *     these are erased from the emitted JS, so SPA bundlers never pull
 *     in the AI provider SDKs or AWS SDK that @ada/llm depends on.
 *   - One transport instance per client; cheap to instantiate.
 *   - The seven sub-namespaces (text, json, embedding, image, video,
 *     tts, model) are plain object literals built in the constructor and
 *     close over the transport, so they share auth/headers/options.
 *   - All eight LLM methods are calls (not notifications); a dispatcher
 *     response of HTTP 204 is treated as a transport-level violation,
 *     not a silent success. Streaming is deferred (see methods.ts).
 *   - JSON-RPC error envelopes are translated to {@link LlmClientError}
 *     with `kind: 'rpc'`; transport problems (HTTP 4xx/5xx, network
 *     errors, unparseable body) become `kind: 'transport'`.
 *   - text.generateWithTools drives the client-tool round-trip loop:
 *     repeatedly POSTs `llm.text.generate`, executing supplied
 *     `ClientTool.execute` functions locally, until the server returns a
 *     `text` result (final answer) or `maxRounds` is exceeded. Server
 *     tools (resolved by name in the LLM service) are transparent — they
 *     never surface as round-trips.
 *
 * Created: 2026-05-06 23:30
 *
 * Changelog:
 *   - 2026-05-06 23:30 — Initial version.
 *   - 2026-05-08 14:00 — Add `text.generateWithTools` and the
 *     `ClientTool` type for client-supplied tools.
 *   - 2026-05-09 18:00 — Slice 1 of P5 streaming: add `text.stream`
 *     wrapping a fetch + SSE parser against `/api/stream`. Returns
 *     `{ textStream, cost, sources }` where `cost` and `sources`
 *     resolve once the corresponding SSE events arrive. Single-turn
 *     only (no tools / conversation) to match the server slice 1.
 *   - 2026-05-09 19:00 — Slice 2 of P5: parse the new `tool-calls`
 *     SSE event (resolves `toolCalls` + `cost` together), accept
 *     tools/conversation/toolResults on `text.stream`, and add
 *     `text.streamWithTools` driving the full agent-loop with local
 *     client-tool execution.
 *   - 2026-05-14 08:30 — Fix a deadlock in `streamWithTools` that left
 *     the copilot widget stuck on "Thinking…": `parseTextStream`'s
 *     `model`/`cost`/`sources`/`toolCalls` promises only resolve once
 *     `textStream` is iterated (iteration is what drives the SSE
 *     parser). The loop awaited `stream.model` BEFORE iterating
 *     `stream.textStream`, so the model promise waited for the parser
 *     and the parser waited for an iterator that never started — the
 *     SSE body sat unread in the browser. Iterate `textStream` first
 *     and yield the `open` event lazily on the first chunk (or, for
 *     pure tool-only rounds with no chunks, after the stream completes).
 *   - 2026-05-16 13:00 — Add the `realtime` namespace
 *     (`session.create`, `tool.execute`, `usage.report`) plus a new
 *     `Transport.notify()` that POSTs a JSON-RPC envelope with no `id`
 *     and tolerates the 204 the dispatcher returns for notifications.
 *     `usage.report` is fire-and-forget by design; the SPA shouldn't
 *     block its WebRTC event loop on every `response.done`. Errors
 *     during the POST are swallowed in `usage.report` and surfaced
 *     in `session.create` / `tool.execute` like every other call.
 */
import { LlmClientError } from './errors.js';
const JSON_RPC_VERSION = '2.0';
class Transport {
    #url;
    #auth;
    #fetch;
    #headers;
    #id;
    constructor(opts) {
        this.#url = opts.url;
        this.#auth = opts.auth ?? { kind: 'none' };
        this.#fetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
        this.#headers = opts.headers ?? {};
        this.#id = opts.idGenerator ?? (() => crypto.randomUUID());
    }
    /**
     * Fire a JSON-RPC notification (envelope without `id`). The
     * dispatcher returns HTTP 204 for batched-empty / pure-notification
     * responses; we tolerate that as success. Errors during the POST are
     * thrown so callers can decide whether to swallow them (the realtime
     * `usage.report` wrapper does so deliberately to keep the SPA's
     * WebRTC event loop non-blocking).
     */
    async notify(method, params, callOpts = {}) {
        const envelope = {
            jsonrpc: JSON_RPC_VERSION,
            method,
            params,
        };
        const res = await this.#post(envelope, method, callOpts);
        if (res.status === 204)
            return;
        // The dispatcher MAY also reply with a JSON-RPC envelope containing
        // an error even for a notification (e.g. credits gate rejection
        // surfaces before the handler runs). Surface those; otherwise drop
        // any unexpected body silently.
        if (res.status >= 400) {
            const body = await this.#parseBody(res, method);
            if ('error' in body) {
                throw new LlmClientError(`${method}: ${body.error.message}`, {
                    kind: 'rpc',
                    method,
                    code: body.error.code,
                    data: body.error.data,
                    httpStatus: res.status,
                });
            }
        }
    }
    async call(method, params, callOpts = {}) {
        const id = callOpts.id ?? this.#id();
        const envelope = {
            jsonrpc: JSON_RPC_VERSION,
            method,
            params,
            id,
        };
        const res = await this.#post(envelope, method, callOpts);
        if (res.status === 204) {
            throw new LlmClientError(`${method}: server returned 204 to a call (notifications-only response)`, { kind: 'transport', method, httpStatus: 204 });
        }
        const body = await this.#parseBody(res, method);
        if ('error' in body) {
            throw new LlmClientError(`${method}: ${body.error.message}`, {
                kind: 'rpc',
                method,
                code: body.error.code,
                data: body.error.data,
                httpStatus: res.status,
            });
        }
        return body.result;
    }
    async #post(envelope, method, callOpts) {
        const headers = {
            'content-type': 'application/json',
            ...this.#headers,
            ...(callOpts.headers ?? {}),
        };
        const authHeader = await this.#authorizationHeader();
        if (authHeader)
            headers.authorization = authHeader;
        let res;
        try {
            res = await this.#fetch(this.#url, {
                method: 'POST',
                headers,
                body: JSON.stringify(envelope),
                signal: callOpts.signal,
            });
        }
        catch (err) {
            throw new LlmClientError(`${method}: network error`, {
                kind: 'transport',
                method,
                cause: err,
            });
        }
        return res;
    }
    async #parseBody(res, method) {
        const text = await res.text();
        if (!res.ok && !text) {
            throw new LlmClientError(`${method}: HTTP ${res.status}`, {
                kind: 'transport',
                method,
                httpStatus: res.status,
            });
        }
        let parsed;
        try {
            parsed = JSON.parse(text);
        }
        catch (err) {
            throw new LlmClientError(`${method}: response was not valid JSON (HTTP ${res.status})`, {
                kind: 'transport',
                method,
                httpStatus: res.status,
                data: text.slice(0, 500),
                cause: err,
            });
        }
        if (parsed === null ||
            typeof parsed !== 'object' ||
            !('jsonrpc' in parsed)) {
            throw new LlmClientError(`${method}: response was not a JSON-RPC envelope (HTTP ${res.status})`, {
                kind: 'transport',
                method,
                httpStatus: res.status,
                data: parsed,
            });
        }
        return parsed;
    }
    async #authorizationHeader() {
        const auth = this.#auth;
        if (auth.kind === 'none')
            return undefined;
        if ('token' in auth)
            return `Bearer ${auth.token}`;
        const token = await auth.getToken();
        return `Bearer ${token}`;
    }
    /**
     * Open an SSE stream by POSTing a JSON-RPC envelope to `<url>/stream`
     * and parsing `event:`/`data:` lines. Returns a {@link TextStreamResponse}.
     * Setup errors (HTTP 4xx/5xx before the stream opens) reject the
     * returned promise. Mid-stream `error` events reject the `textStream`
     * iterator and any unresolved `cost`/`sources` promises.
     */
    async stream(method, params, callOpts = {}) {
        const id = callOpts.id ?? this.#id();
        const envelope = {
            jsonrpc: JSON_RPC_VERSION,
            method,
            params,
            id,
        };
        const headers = {
            'content-type': 'application/json',
            accept: 'text/event-stream',
            ...this.#headers,
            ...(callOpts.headers ?? {}),
        };
        const authHeader = await this.#authorizationHeader();
        if (authHeader)
            headers.authorization = authHeader;
        const streamUrl = `${this.#url}/stream`;
        let res;
        try {
            res = await this.#fetch(streamUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify(envelope),
                signal: callOpts.signal,
            });
        }
        catch (err) {
            throw new LlmClientError(`${method}: network error`, {
                kind: 'transport',
                method,
                cause: err,
            });
        }
        if (!res.ok) {
            const text = await res.text();
            let code = -32603;
            let message = text || `HTTP ${res.status}`;
            try {
                const parsed = JSON.parse(text);
                if (parsed.error?.code !== undefined)
                    code = parsed.error.code;
                if (parsed.error?.message)
                    message = parsed.error.message;
            }
            catch {
                /* non-JSON body, fall back to text */
            }
            throw new LlmClientError(`${method}: ${message}`, {
                kind: 'rpc',
                method,
                code,
                httpStatus: res.status,
            });
        }
        if (!res.body) {
            throw new LlmClientError(`${method}: response has no body`, {
                kind: 'transport',
                method,
                httpStatus: res.status,
            });
        }
        return parseTextStream(method, res.body);
    }
}
/**
 * Typed JSON-RPC client for the LLM service.
 *
 * @example
 * ```ts
 * const client = new LlmClient({
 *   url: '/api',
 *   auth: { kind: 'bearer', getToken: getAccessToken },
 * });
 * const { text, cost } = await client.text.generate({
 *   prompt: 'one-line haiku about credits',
 * });
 * ```
 */
export class LlmClient {
    #transport;
    text;
    json;
    embedding;
    image;
    video;
    tts;
    realtime;
    model;
    constructor(opts) {
        this.#transport = new Transport(opts);
        const t = this.#transport;
        this.text = {
            generate: (params, callOpts) => t.call('llm.text.generate', params, callOpts),
            generateWithTools: (params, callOpts) => runToolLoop(t, params, callOpts),
            stream: (params, callOpts) => t.stream('llm.text.stream', params, callOpts),
            streamWithTools: (params, callOpts) => runStreamLoop(t, params, callOpts ?? {}),
        };
        this.json = {
            generate: (params, callOpts) => t.call('llm.json.generate', params, callOpts),
        };
        this.embedding = {
            generate: (params, callOpts) => t.call('llm.embedding.generate', params, callOpts),
        };
        this.image = {
            generate: (params, callOpts) => t.call('llm.image.generate', params, callOpts),
        };
        this.video = {
            generate: (params, callOpts) => t.call('llm.video.generate', params, callOpts),
        };
        this.tts = {
            generate: (params, callOpts) => t.call('llm.tts.generate', params, callOpts),
        };
        this.realtime = {
            session: {
                create: (params, callOpts) => t.call('llm.realtime.session.create', params, callOpts),
            },
            tool: {
                execute: (params, callOpts) => t.call('llm.realtime.tool.execute', params, callOpts),
            },
            usage: {
                report: async (params, callOpts) => {
                    try {
                        await t.notify('llm.realtime.usage.report', params, callOpts);
                    }
                    catch (err) {
                        callOpts?.onError?.(err);
                    }
                },
            },
        };
        this.model = {
            list: (params, callOpts) => t.call('llm.model.list', params ?? {}, callOpts),
            get: (params, callOpts) => t.call('llm.model.get', params, callOpts),
        };
    }
}
async function runToolLoop(t, params, callOpts = {}) {
    const { clientTools = [], serverTools = [], maxRounds = 5, onStep, toolCallTimeoutMs, ...rest } = params;
    if (maxRounds < 1) {
        throw new LlmClientError('generateWithTools: maxRounds must be >= 1', {
            kind: 'rpc',
            method: 'llm.text.generate',
            code: -32602,
        });
    }
    const toolIndex = new Map();
    for (const tool of clientTools) {
        if (toolIndex.has(tool.name)) {
            throw new LlmClientError(`generateWithTools: duplicate clientTool name "${tool.name}"`, { kind: 'rpc', method: 'llm.text.generate', code: -32602 });
        }
        toolIndex.set(tool.name, tool);
    }
    const tools = [
        ...serverTools.map((s) => ({ kind: 'server', name: s.name, ...(s.args ? { args: s.args } : {}) })),
        ...clientTools.map((c) => ({
            kind: 'client',
            name: c.name,
            description: c.description,
            parameters: c.parameters,
        })),
    ];
    let conversation;
    let toolResults;
    let totalCost = { tokensIn: 0, tokensOut: 0, totalMoney: 0 };
    let rounds = 0;
    for (let round = 0; round < maxRounds; round++) {
        rounds++;
        const turn = await t.call('llm.text.generate', {
            ...rest,
            ...(tools.length > 0 ? { tools } : {}),
            ...(conversation !== undefined ? { conversation } : {}),
            ...(toolResults !== undefined ? { toolResults } : {}),
        }, callOpts);
        totalCost = sumCost(totalCost, turn.cost);
        if (turn.type === 'text') {
            return {
                type: 'text',
                model: turn.model,
                text: turn.text,
                ...(turn.sources ? { sources: turn.sources } : {}),
                cost: totalCost,
                rounds,
            };
        }
        onStep?.({ round, toolCalls: turn.toolCalls });
        conversation = turn.conversation;
        toolResults = await Promise.all(turn.toolCalls.map(async (call) => {
            const tool = toolIndex.get(call.name);
            if (!tool) {
                return {
                    toolCallId: call.toolCallId,
                    isError: true,
                    errorMessage: `Unknown client tool: "${call.name}"`,
                };
            }
            try {
                const result = await runWithTimeout(tool.execute(call.arguments), toolCallTimeoutMs, `Tool "${call.name}" timed out`);
                return { toolCallId: call.toolCallId, result };
            }
            catch (err) {
                return {
                    toolCallId: call.toolCallId,
                    isError: true,
                    errorMessage: err.message,
                };
            }
        }));
    }
    throw new LlmClientError(`generateWithTools: maxRounds (${maxRounds}) exceeded without a text answer`, { kind: 'rpc', method: 'llm.text.generate', code: -32099 });
}
/**
 * Drive `streamWithTools` — multi-round client-tool agent loop with
 * streaming text on the final round. Yields a flat sequence of events
 * across all rounds.
 */
async function* runStreamLoop(t, params, callOpts) {
    const { clientTools = [], serverTools = [], maxRounds = 5, toolCallTimeoutMs, ...rest } = params;
    if (maxRounds < 1) {
        throw new LlmClientError('streamWithTools: maxRounds must be >= 1', {
            kind: 'rpc',
            method: 'llm.text.stream',
            code: -32602,
        });
    }
    const toolIndex = new Map();
    for (const tool of clientTools) {
        if (toolIndex.has(tool.name)) {
            throw new LlmClientError(`streamWithTools: duplicate clientTool name "${tool.name}"`, { kind: 'rpc', method: 'llm.text.stream', code: -32602 });
        }
        toolIndex.set(tool.name, tool);
    }
    const tools = [
        ...serverTools.map((s) => ({
            kind: 'server',
            name: s.name,
            ...(s.args ? { args: s.args } : {}),
        })),
        ...clientTools.map((c) => ({
            kind: 'client',
            name: c.name,
            description: c.description,
            parameters: c.parameters,
        })),
    ];
    let conversation;
    let toolResults;
    let totalCost = { tokensIn: 0, tokensOut: 0, totalMoney: 0 };
    let aggregatedSources = [];
    let collectedText = '';
    let rounds = 0;
    for (let round = 0; round < maxRounds; round++) {
        rounds++;
        const stream = await t.stream('llm.text.stream', {
            ...rest,
            ...(tools.length > 0 ? { tools } : {}),
            ...(conversation !== undefined ? { conversation } : {}),
            ...(toolResults !== undefined ? { toolResults } : {}),
        }, callOpts);
        // IMPORTANT: do NOT `await stream.model` before iterating
        // `stream.textStream`. The parser inside `parseTextStream` is driven
        // by the textStream generator — i.e. the model / cost / sources /
        // toolCalls promises only resolve once *somebody* consumes the SSE
        // body. Awaiting `stream.model` first deadlocks: the model promise
        // waits for the parser, and the parser waits for an iterator that
        // never starts. Iterate textStream first; once the first chunk is in
        // hand the parser has already seen the `open` event and resolved
        // `stream.model`, so awaiting it inside the loop is non-blocking.
        let modelYielded = false;
        let roundText = '';
        for await (const chunk of stream.textStream) {
            if (!modelYielded) {
                const m = await stream.model;
                yield { type: 'open', round, model: m };
                modelYielded = true;
            }
            roundText += chunk;
            yield { type: 'chunk', round, text: chunk };
        }
        // Edge case: a round may produce zero text chunks (e.g. a pure
        // tool-calls round). The parser still resolved `stream.model` on the
        // `open` event during the body sweep above, so awaiting it now is
        // safe; the only way this rejects is if the server returned no
        // `open` event at all, which is a real upstream error worth surfacing.
        if (!modelYielded) {
            const m = await stream.model;
            yield { type: 'open', round, model: m };
            modelYielded = true;
        }
        const [outcome, roundCost, roundSources] = await Promise.all([
            stream.toolCalls,
            stream.cost,
            stream.sources,
        ]);
        totalCost = sumCost(totalCost, roundCost);
        if (roundSources.length > 0)
            aggregatedSources = roundSources;
        if (outcome === null) {
            collectedText = roundText;
            yield {
                type: 'final',
                rounds,
                text: collectedText,
                sources: aggregatedSources,
                cost: totalCost,
            };
            return;
        }
        // Round ended in tool-calls — execute locally and resume.
        yield { type: 'tool-call', round, toolCalls: outcome.toolCalls };
        const results = await Promise.all(outcome.toolCalls.map(async (call) => {
            const tool = toolIndex.get(call.name);
            if (!tool) {
                return {
                    toolCallId: call.toolCallId,
                    isError: true,
                    errorMessage: `Unknown client tool: "${call.name}"`,
                };
            }
            try {
                const result = await runWithTimeout(tool.execute(call.arguments), toolCallTimeoutMs, `Tool "${call.name}" timed out`);
                return { toolCallId: call.toolCallId, result };
            }
            catch (err) {
                return {
                    toolCallId: call.toolCallId,
                    isError: true,
                    errorMessage: err.message,
                };
            }
        }));
        yield { type: 'tool-result', round, results };
        conversation = outcome.conversation;
        toolResults = results;
    }
    throw new LlmClientError(`streamWithTools: maxRounds (${maxRounds}) exceeded without a final answer`, { kind: 'rpc', method: 'llm.text.stream', code: -32099 });
}
function sumCost(a, b) {
    return {
        tokensIn: a.tokensIn + b.tokensIn,
        tokensOut: a.tokensOut + b.tokensOut,
        totalMoney: a.totalMoney + b.totalMoney,
    };
}
async function runWithTimeout(promise, timeoutMs, message) {
    if (!timeoutMs || timeoutMs <= 0)
        return Promise.resolve(promise);
    let timer;
    try {
        return await Promise.race([
            Promise.resolve(promise),
            new Promise((_, reject) => {
                timer = setTimeout(() => reject(new Error(message)), timeoutMs);
            }),
        ]);
    }
    finally {
        if (timer)
            clearTimeout(timer);
    }
}
// ── SSE parsing for text.stream ──────────────────────────────────
function parseTextStream(method, body) {
    let resolveModel;
    let rejectModel;
    let resolveCost;
    let rejectCost;
    let resolveSources;
    let rejectSources;
    let resolveToolCalls;
    let rejectToolCalls;
    const model = new Promise((resolve, reject) => {
        resolveModel = resolve;
        rejectModel = reject;
    });
    const cost = new Promise((resolve, reject) => {
        resolveCost = resolve;
        rejectCost = reject;
    });
    const sources = new Promise((resolve, reject) => {
        resolveSources = resolve;
        rejectSources = reject;
    });
    const toolCalls = new Promise((resolve, reject) => {
        resolveToolCalls = resolve;
        rejectToolCalls = reject;
    });
    // Avoid unhandled-rejection noise when callers ignore one of these.
    void model.catch(() => { });
    void cost.catch(() => { });
    void sources.catch(() => { });
    void toolCalls.catch(() => { });
    let modelResolved = false;
    let costResolved = false;
    let sourcesResolved = false;
    let toolCallsResolved = false;
    async function* textStream() {
        try {
            for await (const { event, data } of parseSSE(body)) {
                switch (event) {
                    case 'open': {
                        const parsed = data ? JSON.parse(data) : {};
                        if (typeof parsed.model === 'string') {
                            resolveModel(parsed.model);
                            modelResolved = true;
                        }
                        break;
                    }
                    case 'chunk': {
                        const parsed = JSON.parse(data);
                        if (typeof parsed.text === 'string')
                            yield parsed.text;
                        break;
                    }
                    case 'sources': {
                        const parsed = JSON.parse(data);
                        resolveSources(parsed.sources ?? []);
                        sourcesResolved = true;
                        break;
                    }
                    case 'cost': {
                        const parsed = JSON.parse(data);
                        if (parsed.cost) {
                            resolveCost(parsed.cost);
                            costResolved = true;
                            resolveToolCalls(null);
                            toolCallsResolved = true;
                        }
                        break;
                    }
                    case 'tool-calls': {
                        const parsed = JSON.parse(data);
                        resolveToolCalls({
                            toolCalls: parsed.toolCalls ?? [],
                            conversation: parsed.conversation ?? [],
                        });
                        toolCallsResolved = true;
                        if (parsed.cost) {
                            resolveCost(parsed.cost);
                            costResolved = true;
                        }
                        // No sources arrive on a tool-calls round.
                        resolveSources([]);
                        sourcesResolved = true;
                        break;
                    }
                    case 'error': {
                        const parsed = JSON.parse(data);
                        const err = new LlmClientError(`${method}: ${parsed.message ?? 'stream error'}`, {
                            kind: 'rpc',
                            method,
                            code: parsed.code ?? -32603,
                        });
                        if (!modelResolved)
                            rejectModel(err);
                        if (!sourcesResolved)
                            rejectSources(err);
                        if (!costResolved)
                            rejectCost(err);
                        if (!toolCallsResolved)
                            rejectToolCalls(err);
                        throw err;
                    }
                    case 'done':
                    default:
                        break;
                }
            }
            if (!sourcesResolved) {
                resolveSources([]);
                sourcesResolved = true;
            }
            if (!modelResolved) {
                rejectModel(new LlmClientError(`${method}: stream ended without open event`, {
                    kind: 'transport',
                    method,
                }));
                modelResolved = true;
            }
            if (!costResolved) {
                rejectCost(new LlmClientError(`${method}: stream ended without cost event`, {
                    kind: 'transport',
                    method,
                }));
                costResolved = true;
            }
            if (!toolCallsResolved) {
                // Stream ended without an explicit cost or tool-calls outcome —
                // treat as final-text so callers don't hang.
                resolveToolCalls(null);
                toolCallsResolved = true;
            }
        }
        catch (err) {
            const wrapped = err instanceof LlmClientError
                ? err
                : new LlmClientError(`${method}: ${err.message}`, {
                    kind: 'transport',
                    method,
                    cause: err,
                });
            if (!modelResolved)
                rejectModel(wrapped);
            if (!sourcesResolved)
                rejectSources(wrapped);
            if (!costResolved)
                rejectCost(wrapped);
            if (!toolCallsResolved)
                rejectToolCalls(wrapped);
            throw wrapped;
        }
    }
    return { textStream: textStream(), model, cost, sources, toolCalls };
}
async function* parseSSE(body) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';
            for (const raw of parts) {
                if (!raw.trim())
                    continue;
                let event = '';
                let data = '';
                for (const line of raw.split('\n')) {
                    if (line.startsWith('event:'))
                        event = line.slice(6).trim();
                    else if (line.startsWith('data:'))
                        data = line.slice(5).trim();
                }
                if (event)
                    yield { event, data };
            }
        }
    }
    finally {
        reader.releaseLock();
    }
}
//# sourceMappingURL=client.js.map